import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { MobxContext } from 'mobx/mobxContext';
import { Box, useTheme } from '@mui/material';
import ReactDOMServer from 'react-dom/server';
import EmployeeTooltip from 'components/tooltips/employeeTooltip';
import MultipleLineChart from 'components/multipleLineChart/multipleLineChart';
import {
  LegendOptions,
  Point,
  Series,
  TooltipFormatterCallbackFunction,
  TooltipFormatterContextObject,
} from 'highcharts';
import ColumnChart from 'components/columnChart/columnChart';
import { UiContext } from 'uiContext';
import { ChartType } from 'components/chartTypeSwitcher/chartTypeSwitcher';

export default observer(function EmployeeChart({
  chartType,
}: {
  chartType: ChartType;
}) {
  const {
    mobxStore: { employeeStore, employeeFormStore },
  } = useContext(MobxContext);

  const {
    spacings: { sp },
  } = useContext(UiContext);

  const theme = useTheme();

  const { adaptedEmployeeLineData, adaptedEmployeeData } = employeeStore;
  const { currentEmployeeId } = employeeStore;

  useEffect(() => {
    const fetchReviewPeriods = async () => {
      if (currentEmployeeId) {
        await employeeFormStore.fetchReviewPeriods(currentEmployeeId);
      }
    };

    fetchReviewPeriods();
  }, [currentEmployeeId, employeeFormStore]);

  const lineChartTooltipFormatter = useCallback(
    function (this: TooltipFormatterContextObject) {
      let root = this;

      if (this.points) {
        root = this.points[0];
      }

      return ReactDOMServer.renderToString(
        <EmployeeTooltip
          colorize={true}
          theme={theme}
          by_lead={root.point?.options.custom?.by_lead}
          by_coworkers={root.point?.options.custom?.by_coworkers}
          by_subordinates={root.point?.options.custom?.by_subordinates}
          avg={root.point?.options.custom?.avg as number | null}
          date={root.point.name}
        />
      );
    },
    [theme]
  );

  const legendParams = useMemo(() => {
    const params: LegendOptions = {
      labelFormatter: function (this: Series | Point) {
        try {
          let seriesName = this?.name;
          if (this?.name === 'by_lead') {
            seriesName = 'Руководитель';
          }
          if (this?.name === 'by_coworkers') {
            seriesName = 'Коллеги';
          }
          if (this?.name === 'by_subordinates') {
            seriesName = 'Подчиненные';
          }
          if (this?.name === 'avg') {
            seriesName = 'Средняя';
          }

          return seriesName;
        } catch (err) {
          console.error(err);
          return '';
        }
      },
      itemMarginBottom: 16,
      enabled: true,
      symbolWidth: 25,
      align: 'center',
      layout: 'horizontal',
      itemStyle: {
        color: 'white',
      },
      itemHoverStyle: {
        color: 'white',
      },
      itemHiddenStyle: {
        color: '#615A69',
      },
    };

    return params;
  }, [theme.palette.text]);

  const columnChartTooltipFormatter: TooltipFormatterCallbackFunction =
    function () {
      const options = this.point.options as any;

      return ReactDOMServer.renderToString(
        <EmployeeTooltip
          theme={theme}
          by_lead={options.by_lead}
          by_coworkers={options.by_coworkers}
          by_subordinates={options.by_subordinates}
          avg={options.y}
          date={options.start_date}
        />
      );
    };

  if (!adaptedEmployeeLineData || !adaptedEmployeeData) {
    return null;
  }

  return (
    <Box sx={{ px: sp.s60 }}>
      {chartType === 'col' ? (
        <ColumnChart
          chartData={adaptedEmployeeData}
          tooltipOptionsArgs={{
            formatter: columnChartTooltipFormatter,
            outside: true,
            padding: 16,
          }}
          plotOptionsArgs={{}}
        />
      ) : (
        <MultipleLineChart
          series={adaptedEmployeeLineData}
          customTooltipFormatter={lineChartTooltipFormatter}
          yAxisOptions={{
            tickInterval: 0.2,
          }}
          customLegend={legendParams}
        />
      )}
    </Box>
  );
});
